import React, { useMemo } from 'react';
import { View, Text, Animated, Pressable, Platform } from 'react-native';
import { Zap, BarChart2, List } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import type { getMatchCardStyles } from './MatchCard.styles';
import { MatchCardHeroShell } from './MatchCardHeroShell';
import { resolveMatchCardOutcomeStyles } from './matchCardOutcome.helper';
import { getFinishedScoreDisplay } from './matchCardFinishedScore.helper';
import { MATCH_CARD_COPY } from './MatchesView.constants';
import type { Theme } from '../../../src/theme/getTheme';
import type { WorldCupMatch } from '../../../src/worldcup/types/worldcup.types';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';

interface MatchCardFinishedProps {
  match: WorldCupMatch;
  isBoosted: boolean;
  stageBadgeText: string;
  homeRank: string | null;
  awayRank: string | null;
  cardScale: { transform: { scale: Animated.AnimatedInterpolation<number> }[] };
  isExactHit: boolean;
  isCorrectResult: boolean;
  hasPrediction: boolean;
  earnedPoints: number;
  /** Finished match whose pick the server hasn't settled yet (pointsEarned still null) — show "Scoring…" not a number. */
  isPointsPending?: boolean;
  /** Late joiner: this match kicked off before they joined, so they hold no pick (forfeited → 0). */
  isMissedPreJoin?: boolean;
  styles: ReturnType<typeof getMatchCardStyles>;
  theme: Theme;
  predictionLabel?: string;
  onFixturePress?: (matchId: string) => void;
  onStatsPress?: (matchId: string) => void;
}

export const MatchCardFinished: React.FC<MatchCardFinishedProps> = ({
  match,
  isBoosted,
  stageBadgeText,
  homeRank,
  awayRank,
  cardScale,
  isExactHit,
  isCorrectResult,
  hasPrediction,
  earnedPoints,
  isPointsPending = false,
  isMissedPreJoin = false,
  styles,
  theme,
  predictionLabel = 'You',
  onFixturePress,
  onStatsPress,
}) => {
  const homeKitFallback = useMemo(() => getTeamKitConfig(match.homeTeam), [match.homeTeam]);
  const awayKitFallback = useMemo(() => getTeamKitConfig(match.awayTeam), [match.awayTeam]);
  const scoreDisplay = useMemo(() => getFinishedScoreDisplay(match), [match]);

  const {
    cardBorder: finishedCardBorder,
    accentBar: accentBarStyle,
    outcomeLabelStyle,
    outcomeLabel,
    boostGlow: finishedBoostGlowStyle,
    boostBadgeStyle: finishedBoostBadgeStyle,
    boostBadgeTextStyle: finishedBoostBadgeTextStyle,
    boostZapColor: finishedBoostZapColor,
  } = resolveMatchCardOutcomeStyles(isExactHit, isCorrectResult, isBoosted, styles, theme);

  return (
    <Animated.View style={cardScale}>
      <View style={[styles.finishedCard, finishedCardBorder, finishedBoostGlowStyle]}>
        <MatchCardHeroShell />
        <View style={styles.finishedContent}>
          <View style={[styles.accentBar, accentBarStyle]} />
          <View style={styles.finishedTopRow}>
            <View style={styles.stageRow}>
              <Text style={styles.finishedStageBadge}>{stageBadgeText}</Text>
              {isBoosted && (
                <View style={[styles.boostMiniBadge, finishedBoostBadgeStyle]}>
                  <Zap size={10} color={finishedBoostZapColor} />
                  <Text style={[styles.boostMiniBadgeText, finishedBoostBadgeTextStyle]}>x2</Text>
                </View>
              )}
            </View>
            <Text style={styles.fullTimeText}>Full Time</Text>
          </View>

          <View style={styles.finishedMatchRow}>
            <View style={styles.finishedTeamBlock}>
              <View style={styles.flagWrapper}>
                <CountryFlag countryCode={match.homeCountryCode} kitFallback={homeKitFallback} accessibilityLabel={`${match.homeTeam} flag`} />
              </View>
              <Text style={styles.finishedTeamName} numberOfLines={1} ellipsizeMode="tail">
                {match.homeTeam}
              </Text>
              {homeRank != null && <Text style={styles.groupPosition}>{homeRank}</Text>}
            </View>
            <View style={styles.finishedScoreBlock}>
              {scoreDisplay.kind === 'single' ? (
                <View style={styles.finishedScoreRow}>
                  <Text style={styles.finishedScoreNum}>{scoreDisplay.home}</Text>
                  <Text style={styles.finishedScoreDash}>–</Text>
                  <Text style={styles.finishedScoreNum}>{scoreDisplay.away}</Text>
                </View>
              ) : (
                <View style={styles.finishedScoreStacked}>
                  {/* Final score (incl. extra time) — large, primary. */}
                  <View style={styles.finishedScoreRow}>
                    <Text style={styles.finishedScoreNum}>{scoreDisplay.final.home}</Text>
                    <Text style={styles.finishedScoreDash}>–</Text>
                    <Text style={styles.finishedScoreNum}>{scoreDisplay.final.away}</Text>
                  </View>
                  {scoreDisplay.penalty && (
                    <Text style={styles.finishedScorePens}>
                      {`${scoreDisplay.penalty.home}–${scoreDisplay.penalty.away} pens`}
                    </Text>
                  )}
                  {/* 90-minute result the pick was scored on — smaller, below. */}
                  <View style={styles.finishedRegulationLine}>
                    <Text style={styles.finishedRegulationLabel}>90 min</Text>
                    <Text style={styles.finishedRegulationScore}>
                      {`${scoreDisplay.regulation.home}–${scoreDisplay.regulation.away}`}
                    </Text>
                  </View>
                </View>
              )}
            </View>
            <View style={styles.finishedTeamBlock}>
              <View style={styles.flagWrapper}>
                <CountryFlag
                  countryCode={match.awayCountryCode}
                  kitFallback={awayKitFallback}
                  accessibilityLabel={`${match.awayTeam} flag`}
                />
              </View>
              <Text style={styles.finishedTeamName} numberOfLines={1} ellipsizeMode="tail">
                {match.awayTeam}
              </Text>
              {awayRank != null && <Text style={styles.groupPosition}>{awayRank}</Text>}
            </View>
          </View>

          {hasPrediction && (
            <View style={styles.finishedFooter}>
              <View style={styles.finishedFooterLeft}>
                <Text style={styles.finishedPredictionText}>
                  {`${predictionLabel}: ${match.prediction!.home}–${match.prediction!.away}`}
                </Text>
                <Text style={[styles.finishedOutcomeLabel, outcomeLabelStyle]}>{outcomeLabel}</Text>
              </View>
              {isPointsPending ? (
                <Text style={styles.finishedPointsPending}>{MATCH_CARD_COPY.SCORING}</Text>
              ) : earnedPoints > 0 ? (
                <Text style={[styles.finishedPointsPos, isExactHit ? styles.finishedPointsHit : styles.finishedPointsResult]}>
                  {`+${earnedPoints}`}
                </Text>
              ) : (
                <Text style={styles.finishedPointsNeg}>+0</Text>
              )}
            </View>
          )}

          {!hasPrediction && isMissedPreJoin && (
            <View style={styles.finishedMissedRow}>
              <Text style={styles.finishedMissedText}>{MATCH_CARD_COPY.JOINED_MID_TOURNAMENT}</Text>
            </View>
          )}

          <View style={styles.bottomRow}>
            <Pressable
              onPress={() => onStatsPress?.(match.id)}
              style={({ pressed }) => [
                styles.cardActionButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.cardActionButtonPressed,
              ]}
            >
              <BarChart2 size={13} color={styles.cardActionIcon.color as string} strokeWidth={2} />
              <Text style={styles.cardActionText}>Stats</Text>
            </Pressable>

            <View style={styles.cardActionDivider} />

            <Pressable
              onPress={() => onFixturePress?.(match.id)}
              style={({ pressed }) => [
                styles.cardActionButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.cardActionButtonPressed,
              ]}
            >
              <List size={13} color={styles.cardActionIcon.color as string} strokeWidth={2} />
              <Text style={styles.cardActionText}>Match Details</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};
